import { FontAwesomeIcon } from "components/FontAwesomeIcon";
import { useDomainHandler } from "hooks/useDomainHandler";
import { type FC, type KeyboardEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import { onPaste } from "./MultipleEmailInput.helper";
import {
  StyledEmailBox,
  StyledEmailInput,
  StyledEmailInputBox,
  StyledEmailsBox,
  StyledInvalidEmail,
  StyledRemoveEmailIcon,
} from "./MultipleEmailInput.styled";

export interface MultipleEmailInputProps {
  dataCy?: string;
  width?: string;
  height?: string;
  selectedEmails: EmailType[];
  onSelectedEmailsChanged: (emails: EmailType[]) => void;
  isForTeachers?: boolean;
}

export type EmailType = {
  email: string;
  isIncorrect: boolean;
};

const emailSchema = object({
  email: string().email(),
});

export const MultipleEmailInput: FC<MultipleEmailInputProps> = ({
  dataCy,
  width = "100%",
  height = "15rem",
  selectedEmails,
  onSelectedEmailsChanged,
  isForTeachers,
}) => {
  const [emailList, setEmailList] = useState<EmailType[]>(selectedEmails);
  const [newEmail, setNewEmail] = useState<string>("");
  const { getStudentTermByDomain, getTeacherTermByDomain } = useDomainHandler();

  const { t } = useTranslation("common");
  const { t: tToast } = useTranslation("toast");

  useEffect(() => {
    onSelectedEmailsChanged(emailList);
  }, [emailList]);

  const updateEmailList = (emails: string[]) => {
    const uniqueEmails = Array.from(new Set(emails.filter((email) => !!email)));
    const newEmails: EmailType[] = uniqueEmails.map((email) => ({
      email: email.trim(),
      isIncorrect: !emailSchema.isValidSync({ email }),
    }));
    setEmailList([...emailList, ...newEmails]);
  };

  const onKeyDownHandler = async (event: KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.ctrlKey && event.key === "v") {
      await onPaste(tToast, updateEmailList);
    }
  };

  const onRemoveEmailFromList = (email: string) => {
    setEmailList(emailList.filter((currEmail) => currEmail.email !== email));
  };

  return (
    <>
      <StyledEmailsBox
        contentEditable
        suppressContentEditableWarning
        alignContent="flex-start"
        data-cy={dataCy || "create-account-emails"}
        flexWrap="wrap"
        height={height}
        mt={2}
        padding={1}
        width={width}
        onKeyDown={(e) => onKeyDownHandler(e)}
        onPaste={(e) => onPaste(tToast, updateEmailList, e)}
      >
        {emailList.length === 0 &&
          t("multipleEmailInput.pasteArea.placeholder", {
            userTerm: isForTeachers
              ? getTeacherTermByDomain({ usePlural: true })
              : getStudentTermByDomain({ usePlural: true }),
          })}
        {emailList.map((emailType) => (
          <StyledEmailBox
            key={`wrapper-${emailType.email}`}
            isIncorrect={!emailSchema.isValidSync({ email: emailType.email })}
          >
            <StyledRemoveEmailIcon
              key={`icon-${emailType.email}`}
              data-cy="remove-email-button"
              iconName="fal fa-times"
              onClick={() => onRemoveEmailFromList(emailType.email)}
            />
            <span key={emailType.email}>{emailType.email}</span>
          </StyledEmailBox>
        ))}
        {emailList.some((emailType) => emailType.isIncorrect) && (
          <StyledInvalidEmail>{t("multipleEmailInput.error.invalidEmail")}</StyledInvalidEmail>
        )}
      </StyledEmailsBox>
      <StyledEmailInputBox
        alignItems="center"
        justifyContent="space-between"
        padding={1}
        width={width}
      >
        <StyledEmailInput
          data-cy="manually-input-email"
          placeholder={t("multipleEmailInput.manualInput.placeholder")}
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <FontAwesomeIcon
          data-cy="add-manual-email"
          iconName="far fa-plus"
          onClick={() => {
            updateEmailList([newEmail]);
            setNewEmail("");
          }}
        />
      </StyledEmailInputBox>
    </>
  );
};
